import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { useTextEditor } from "../hooks/useTextEditor";
import { NoticiaSchema } from "../../domain/entities/Noticia";
import styles from "../../styles/NewsForm.module.css";
import UploadField from "./UploadField";
import Loading from "../../../../components/load/Loading";

/* ------------------------------------------------------------------ */
/* Utilidades de edición                                               */
/* ------------------------------------------------------------------ */

// El backend puede guardar fijado como 0/1, "true"/"false" o booleano.
const toBool = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

// Normaliza textos para comparar ("", null y undefined quedan equivalentes).
const norm = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

// Igual que norm, pero colapsa espacios/múltiples saltos de línea
// (el HTML guardado y el texto plano del editor no siempre coinciden
// exactamente en los espacios).
const normText = (value) => norm(value).replace(/\s+/g, " ");

// Fecha ISO del backend -> formato local para <input type="datetime-local">.
// (Un valor ISO con "Z" no es válido para ese input, se vería vacío.)
const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Compara fechas entendiendo ambos formatos (ISO del backend y datetime-local).
const fechasIguales = (a, b) => {
  const left = norm(a);
  const right = norm(b);
  if (!left || !right) return left === right;
  const timeA = new Date(left).getTime();
  const timeB = new Date(right).getTime();
  if (Number.isNaN(timeA) || Number.isNaN(timeB)) return left === right;
  return timeA === timeB;
};

// HTML guardado -> texto plano, para comparar contra el contenido actual
// del editor (draft-js trabaja con texto plano internamente).
const htmlToPlain = (html) => {
  if (!html) return "";
  try {
    const blocks = convertFromHTML(String(html));
    if (blocks.contentBlocks && blocks.contentBlocks.length > 0) {
      return ContentState.createFromBlockArray(
        blocks.contentBlocks,
        blocks.entityMap,
      ).getPlainText();
    }
  } catch (error) {
    // Si draft-js no puede convertirlo, usamos el respaldo de abajo.
  }
  return String(html).replace(/<[^>]*>/g, " ");
};

// Detecta si un valor del formulario contiene archivos recién seleccionados.
const containsFile = (value) => {
  if (typeof File !== "undefined" && value instanceof File) return true;
  if (!value || typeof value !== "object") return false;
  if (typeof value.length !== "number") return false;
  return Array.from(value).some(
    (item) => typeof File !== "undefined" && item instanceof File,
  );
};

const fileNameFromUrl = (url) => {
  const clean = String(url).split("?")[0].split("#")[0];
  const name = clean.substring(clean.lastIndexOf("/") + 1);
  try {
    return decodeURIComponent(name) || String(url);
  } catch (error) {
    return name || String(url);
  }
};

const isDocUrl = (url) =>
  /\.(pdf|ppt|pptx|pptm)$/i.test(String(url || "").split("?")[0]);

// Convierte el archivo(s) existente de la noticia al formato que lista UploadField.
const toExistingFiles = (value, thumb) => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list.filter(Boolean).map((url) => ({
    url,
    name: fileNameFromUrl(url),
    // Si es un documento (pdf/ppt) usamos la imagen de vista previa como miniatura.
    thumb: isDocUrl(url) ? thumb || null : url,
    size: null,
  }));
};

// Valores base del formulario (react-hook-form solo los lee al montar).
const buildInitialValues = (defaults) => {
  const values = { ...defaults };
  values.fijado = toBool(values.fijado);
  if (values.fecha_publicacion) {
    values.fecha_publicacion = toDateTimeLocal(values.fecha_publicacion);
  }
  // El esquema Zod exige string: un id numérico del backend bloquearía
  // el submit sin mostrar ningún mensaje de error visible.
  if (values.id_img !== undefined && values.id_img !== null) {
    values.id_img = String(values.id_img);
  }
  return values;
};

/* ------------------------------------------------------------------ */
/* Componente                                                          */
/* ------------------------------------------------------------------ */

const NewsForm = ({ mode, defaultValues = {}, onSubmit }) => {
  // Nota: react-hook-form ignora los cambios posteriores de defaultValues,
  // por eso el padre (OpcNews) debe montar este componente con el item
  // ya seleccionado y reiniciarlo al cambiar de noticia.
  const initialValues = useMemo(
    () => buildInitialValues(defaultValues),
    [defaultValues],
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: initialValues,
    resolver: zodResolver(NoticiaSchema),
  });

  const { editorState, onEditorStateChange, setEditorState } = useTextEditor();
  const editorLoadedRef = useRef(false);

  const hasExistingPreview = mode === "edit" && Boolean(defaultValues.preview);
  const [showPreviewField, setShowPreviewField] = useState(
    () =>
      mode === "edit" &&
      (hasExistingPreview || isDocUrl(defaultValues.img)),
  );
  const [newDocSelected, setNewDocSelected] = useState(false);

  // Archivos que la noticia ya tiene guardados (para listar en UploadField).
  const existingImgFiles = useMemo(
    () =>
      mode === "edit"
        ? toExistingFiles(defaultValues.img, defaultValues.preview)
        : [],
    [mode, defaultValues],
  );

  const existingPreviewFiles = useMemo(
    () => (hasExistingPreview ? toExistingFiles(defaultValues.preview) : []),
    [hasExistingPreview, defaultValues],
  );

  // La vista previa solo es obligatoria si hay que subir una nueva.
  const previewRequired =
    showPreviewField &&
    (mode === "create" || (newDocSelected && !hasExistingPreview));

  useEffect(() => {
    if (mode !== "edit" || editorLoadedRef.current) return;

    const html = defaultValues.descripcion;
    if (!html) {
      editorLoadedRef.current = true;
      return;
    }

    try {
      const blocks = convertFromHTML(html);
      const state = EditorState.createWithContent(
        ContentState.createFromBlockArray(
          blocks.contentBlocks,
          blocks.entityMap,
        ),
      );
      setEditorState(state);
      editorLoadedRef.current = true;
    } catch (error) {
      console.error("Error converting HTML to EditorState:", error);
      // Si no pudimos cargar la descripción original, marcamos el editor
      // como no cargado para NO interpretar su contenido vacío como un
      // "cambio" y borrar accidentalmente la descripción guardada.
    }
  }, [mode, defaultValues, setEditorState]);

  const onFileChange = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      const isPdf = file.type === "application/pdf";
      const isPpt =
        file.type === "application/vnd.ms-powerpoint" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation";
      const isDoc = isPdf || isPpt;
      setShowPreviewField(isDoc || hasExistingPreview);
      setNewDocSelected(isDoc);
      setValue("image_preview", "", { shouldValidate: false });
    } else {
      setShowPreviewField(hasExistingPreview);
      setNewDocSelected(false);
    }
  };

  const submit = handleSubmit(async (data) => {
    const currentPlain = editorState.getCurrentContent().getPlainText();

    if (mode === "edit") {
      const originalPlain = htmlToPlain(defaultValues.descripcion);

      const textFields = ["titulo", "bajada", "etiqueta", "tipo", "url"];
      const textChanged = textFields.some(
        (field) => norm(data[field]) !== norm(defaultValues[field]),
      );
      const fijadoChanged =
        toBool(data.fijado) !== toBool(defaultValues.fijado);
      const fechaChanged = !fechasIguales(
        data.fecha_publicacion,
        defaultValues.fecha_publicacion,
      );
      const descripcionChanged =
        editorLoadedRef.current &&
        normText(currentPlain) !== normText(originalPlain);

      const hasNewImg = containsFile(data.img);
      const hasNewPreview = containsFile(data.image_preview);

      const changed =
        textChanged ||
        fijadoChanged ||
        fechaChanged ||
        descripcionChanged ||
        hasNewImg ||
        hasNewPreview;

      if (!changed) {
        toast.error("No has realizado ningún cambio.");
        return;
      }

      // Conservamos los valores originales en los campos que no se tocaron.
      if (!fechaChanged && defaultValues.fecha_publicacion) {
        data.fecha_publicacion = defaultValues.fecha_publicacion;
      }
      data.descripcion = descripcionChanged
        ? currentPlain
        : (defaultValues.descripcion ?? null);
    } else {
      data.descripcion = currentPlain;
    }

    await onSubmit(data);
  });

  return (
    <form onSubmit={submit} className={styles.newsForm}>
      <h2 className="title-section-promotions text-center"> {mode === "create" ? "Crear noticia" : "Actualizando noticia"} </h2>
      <div className={styles.group}>
        <label htmlFor="titulo">Título</label>
        <input
          id="titulo"
          {...register("titulo", { required: "Título es obligatorio" })}
        />
        {errors.titulo && (
          <p className={styles.errorMessage}>{errors.titulo.message}</p>
        )}
      </div>

      <div className={styles["group-select"]}>
        <div className={styles.group}>
          <label htmlFor="etiqueta">Categoría</label>
          <select
            id="etiqueta"
            {...register("etiqueta", { required: "Categoría es obligatoria" })}
          >
            <option value="">Seleccione una categoría</option>
            <option value="Comunicación Interna">Comunicación Interna</option>
            <option value="Fechas especiales">Fechas especiales</option>
            <option value="Promociones">Promociones</option>
            <option value="Universidad corporativa">
              Universidad corporativa
            </option>
            <option value="Talento Humano">Talento Humano</option>
          </select>
          {errors.etiqueta && (
            <p className={styles.errorMessage}>{errors.etiqueta.message}</p>
          )}
        </div>

        <div className={styles.group}>
          <label htmlFor="tipo">Tipo de publicación</label>
          <select
            id="tipo"
            {...register("tipo", { required: "Tipo es obligatorio" })}
          >
            <option value="">Seleccione un tipo</option>
            <option value="Pop up">Pop up</option>
            <option value="noticia interna">Noticia interna</option>
          </select>
          {errors.tipo && (
            <p className={styles.errorMessage}>{errors.tipo.message}</p>
          )}
        </div>
      </div>

      {watch("tipo") === "noticia interna" && (
        <div className={styles.group}>
          <label htmlFor="bajada">Bajada</label>
          <input id="bajada" {...register("bajada")} />
        </div>
      )}

      <div className={styles.group}>
        <label>Descripción</label>
        <Editor
          editorState={editorState}
          onEditorStateChange={(state) => onEditorStateChange(state, setValue)}
        />
      </div>

      <UploadField
        name="img"
        label="Archivo principal"
        accept={["image/*", ".pdf", ".ppt", ".pptx"]}
        register={register}
        setValue={setValue}
        required={mode === "create"}
        maxFiles={1}
        onChangeFiles={onFileChange}
        error={errors.img}
        existingFiles={existingImgFiles}
      />

      {showPreviewField && (
        <UploadField
          name="image_preview"
          label="Imagen de Vista Previa"
          accept={["image/*"]}
          register={register}
          setValue={setValue}
          required={previewRequired}
          maxFiles={1}
          error={errors.image_preview}
          existingFiles={existingPreviewFiles}
        />
      )}

      <div className={styles["group-select"]}>
        {/* FECHA */}
        <div className={styles.group}>
          <label htmlFor="fecha_publicacion">Programar publicación</label>

          <span className={styles.fieldHint}>
            Opcional. Si no seleccionas una fecha, se publicará inmediatamente.
          </span>

          <div className={styles.dateInputWrapper}>
            <input
              id="fecha_publicacion"
              type="datetime-local"
              {...register("fecha_publicacion")}
            />
          </div>
        </div>

        {/* SWITCH */}
        <div className={styles.group}>
          <label>Fijar publicación</label>

          <span className={styles.fieldHint}>
            Mantener la publicación destacada.
          </span>

          <label className={styles.switchContainer}>
            <input id="fijado" type="checkbox" {...register("fijado")} />

            <span className={styles.slider}></span>

            <span className={styles.switchStatus}>
              <span className={styles.switchOff}>No</span>
              <span className={styles.switchOn}>Sí</span>
            </span>
          </label>
        </div>
      </div>

      <div className={styles.group}>
        <label>Link</label>
        <input type="text" {...register("url")} />
      </div>

      <input type="hidden" id="id" {...register("id")} />
      <input type="hidden" id="id_img" {...register("id_img")} />

      <button type="submit" disabled={isSubmitting} className="btn-login">
        {isSubmitting
          ? mode === "edit"
            ? "Actualizando noticia…"
            : "Subiendo noticia…"
          : mode === "edit"
            ? "Actualizar"
            : "Guardar"}
      </button>

      {/* Overlay de carga: mientras se sube el archivo / se envía la
          petición, se muestra para dejar claro que la acción está ocurriendo. */}
      {isSubmitting && (
        <div className={styles.formOverlay}>
          <div className={styles.formOverlayContent}>
            <Loading />
            <p className={styles.formOverlayText}>
              {mode === "edit"
                ? "Actualizando noticia…"
                : "Subiendo noticia…"}
            </p>
          </div>
        </div>
      )}
    </form>
  );
};

export default NewsForm;
